const SITE_ORIGIN = "https://onecommunications.io"

type DreamBrokerVideo = {
	contentUrl?: string
	embedUrl?: string
	posterUrl?: string
}

export type VideoObjectInput = {
	name?: string
	description?: string
	thumbnailSrc?: string
	videoSrc?: string
	uploadDate?: string
}

const stripHtml = (value: string): string => {
	return value
		.replace(/<[^>]+>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim()
}

const absoluteUrl = (src: string): string => {
	if (src.startsWith("//")) return `https:${src}`
	if (src.startsWith("http://") || src.startsWith("https://")) return src
	if (src.startsWith("/")) return `${SITE_ORIGIN}${src}`
	return src
}

const dreamBrokerVideo = (src: string): DreamBrokerVideo => {
	try {
		const url = new URL(
			src.startsWith("//") ? `https:${src}` : src,
			"https://dreambroker.com",
		)
		const match = url.pathname.match(
			/\/channel\/([a-zA-Z0-9]+)\/iframe\/([a-zA-Z0-9]+)/,
		)
		if (!match) {
			const fileMatch = url.pathname.match(
				/\/channel\/([a-zA-Z0-9]+)\/([a-zA-Z0-9]+)\/get\/[^/]+\.mp4$/,
			)
			if (fileMatch) {
				const [, channelId, videoId] = fileMatch
				return {
					contentUrl: `https://dreambroker.com${url.pathname}`,
					posterUrl: `https://dreambroker.com/channel/${channelId}/${videoId}/get/poster.png`,
				}
			}
			if (url.pathname.endsWith(".mp4")) {
				return {
					contentUrl: `${url.origin}${url.pathname}`,
				}
			}
			return {}
		}

		const [, channelId, videoId] = match
		return {
			contentUrl: `https://dreambroker.com/channel/${channelId}/iframe/${videoId}/normal.mp4`,
			embedUrl: `https://dreambroker.com/channel/${channelId}/iframe/${videoId}`,
			posterUrl: `https://dreambroker.com/channel/${channelId}/${videoId}/get/poster.png`,
		}
	} catch {
		return {}
	}
}

export const videoPosterUrl = (src?: string): string | undefined => {
	if (!src) return undefined
	return dreamBrokerVideo(src).posterUrl
}

export const buildVideoObject = (
	input: VideoObjectInput,
): Record<string, string> | null => {
	if (!input.videoSrc) return null

	const video = dreamBrokerVideo(input.videoSrc)
	const thumbnailUrl = input.thumbnailSrc
		? absoluteUrl(input.thumbnailSrc)
		: video.posterUrl
	const name = input.name ? stripHtml(input.name) : ""

	if (!name || !thumbnailUrl || !video.contentUrl) return null

	const schema: Record<string, string> = {
		"@context": "https://schema.org",
		"@type": "VideoObject",
		name,
		thumbnailUrl,
		contentUrl: video.contentUrl,
	}

	const description = input.description ? stripHtml(input.description) : ""
	if (description) schema.description = description.slice(0, 300)
	if (input.uploadDate) schema.uploadDate = input.uploadDate
	if (video.embedUrl) schema.embedUrl = video.embedUrl

	return schema
}

export const videoObjectJson = (
	schema: Record<string, string> | null,
): string | null => {
	if (!schema) return null
	return JSON.stringify(schema).replace(/</g, "\\u003c")
}
